<!-- BenchmarksModal.vue -->
<template>
  <div class="modal-container">
    <img
      src="images/close.svg"
      class="close"
      @click="resetModal"
      role="button"
      aria-label="Close"
      alt="X to close modal"
      title="Close"
    />
    <div class="modal-content">
      <div>
        <transition-group :name="titleFade">
          <h2 :key="title">{{ title }}</h2>
        </transition-group>
      </div>
      <div class="modal-main">
        <knot-carousel
          :use-slide-names="true"
          :slide-class="slideClass"
          @page-changed="pageChange"
        >
          <template v-slot:Overall>
            <div class="centered-text">
              <img
                src="images/benchmarks.svg"
                class="stats-img"
                alt="Rectangular Golden Spiral"
                title="Benchmarks"
              />
            </div>
            <template v-if="benchmarkData.overall === null">
              <div class="spinner">
                <img src="images/spiral.svg" alt="spiral" title="Loading" />
                <div class="large">
                  Loading Benchmarks for Puzzle No. {{ puzzleNumber }}
                </div>
              </div>
            </template>
            <template v-else-if="benchmarkDataAvailable('overall')">
              <h2 class="important-note centered-text">
                {{ displayDate }}
              </h2>
              <h2 class="centered-text">Overall Player Preferences</h2>

              <div
                class="centered-text"
                v-html="overallBenchmarksStatement()"
              ></div>
              <table id="overall-benchmarks">
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="mode in modes" :key="mode">{{ mode }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="level in levels" :key="level">
                    <th class="overall-level-label">{{ level }}</th>
                    <td v-for="mode in modes" :key="mode">
                      <div
                        class="overall-cell"
                        :class="getOverallBenchmarkClass(level, mode)"
                      >
                        {{ getOverallBenchmark(level, mode) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>

            <template v-else>
              <div class="important-note centered-text">
                Not enough players have completed Puzzle
                <strong>No. {{ puzzleNumber }}</strong
                >.
              </div>
              <div class="important-note centered-text">Check back later.</div>
            </template>
          </template>
          <template v-slot:Matching>
            <template v-if="benchmarkData?.matching === null">
              <div class="spinner">
                <img src="images/spiral.svg" alt="spiral" title="Loading" />
                <div class="large">
                  Loading Matching Benchmarks for Puzzle No. {{ puzzleNumber }}
                </div>
              </div>
            </template>
            <template v-else>
              <div class="centered-objects">
                <knot-select
                  :items="metricTitles"
                  @select-item="metricSelectTitle"
                  :defaultItem="selectedMetricTitle"
                  :horizontal="false"
                  :itemWidth="22"
                  :itemHeight="3"
                  id="metric"
                  :activeSelect="activeSelect"
                ></knot-select>
              </div>
              <template v-if="benchmarkDataAvailable(selectedMetric)">
                <div class="benchmark-description-container">
                  <transition name="metric-description">
                    <div
                      class="benchmark-description"
                      :key="selectedMetricDescription.key"
                    >
                      <div v-html="selectedMetricDescription.desc"></div>
                    </div>
                  </transition>
                </div>
                <benchmarks-table
                  :player-result="playerMetricResult"
                  :metric-data="benchmarkData?.matching?.[selectedMetric]"
                  :metric="selectedMetric"
                  :max-benchmark-labels="maxBenchmarkLabels"
                >
                </benchmarks-table>
                <div class="speed-benchmark-container">
                  <transition name="flatten">
                    <div
                      class="speed-benchmark"
                      v-if="speedBenchmark && selectedMetric === 'play_time'"
                    >
                      <div class="label">Top {{ speedBenchmark }}%</div>
                      <div
                        class="description"
                        v-html="matchingBenchmarkStatement('speed')"
                      ></div>
                    </div>
                  </transition>
                </div>
              </template>
              <template v-else>
                <div class="important-note centered-text">
                  Not enough players have completed Puzzle
                  <strong>No. {{ puzzleNumber }}</strong
                  >. at Level {{ status.level }} in {{ status.mode }}
                  <template v-if="selectedMetric === 'play_time'">
                    Mode without hints.
                  </template>
                  <template v-else>Mode.</template>
                </div>
              </template>
            </template>
          </template>
        </knot-carousel>
      </div>
    </div>
  </div>
</template>

<script>
import { MODE, NUM_TO_STR } from "../types.js";
import BenchmarksTable from "./BenchmarksTable.vue";
function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function numberToString(input) {
  try {
    if (input < 10) return NUM_TO_STR[parseInt(input)];
    else return numberWithCommas(input);
  } catch {
    return input;
  }
}
export default {
  components: { BenchmarksTable },
  name: "BenchmarksModal",
  props: {
    resetModal: {
      type: Function,
      required: true,
    },
    status: {
      type: Object,
      required: true,
    },
    puzzleCompleted: {
      type: Boolean,
      required: true,
    },
    levels: {
      type: Array,
      required: true,
    },
    elapsedTimeFormater: {
      type: Function,
      required: true,
    },
    puzzleTime: {
      type: Number,
      required: true,
    },
    puzzleNumber: {
      type: Number,
      required: true,
    },
    displayDate: {
      type: String,
      required: true,
    },
    isArchivePlay: {
      type: Boolean,
      required: true,
    },
    benchmarkData: {
      type: Object,
      required: true,
    },
    foundSequencesCount: {
      type: Number,
      required: true,
    },
    totalErrors: {
      type: Number,
      required: true,
    },
    revealedHints: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      title: "",
      titleFade: "",
      slideClass: { All: "", Overall: "" },
      modes: MODE,
      selectedMetric: null,
      selectedMetricTitle: null,
      metricTitlesLookup: {
        play_time: "Play Time",
        found_sequences: "Sequences",
        hints_used: "Hints",
        error: "Errors",
      },
      activeSelect: "",
    };
  },
  created() {
    this.title = `Puzzle No. ${this.puzzleNumber} Benchmarks`;
  },
  methods: {
    metricSelectTitle(metricTitle, id, active) {
      if (active) {
        this.activeSelect = id;
      } else {
        this.activeSelect = "";
      }
      this.selectedMetric = Object.keys(this.metricTitlesLookup).find(
        (key) => this.metricTitlesLookup[key] === metricTitle
      );
      this.selectedMetricTitle = metricTitle;
    },
    playerTimeBin() {
      if (this.revealedHints > 0) {
        return "NA";
      }
      const minutes = Math.floor(this.puzzleTime / 60);
      if (minutes === 0) {
        return "<1m";
      }
      if (minutes >= 5) {
        return "5m+";
      }
      return `${minutes}-${minutes + 1}m`;
    },
    inTimeBin(header) {
      if (this.revealedHints > 0) {
        return false;
      }
      if (header === "<1m") {
        return this.puzzleTime < 60;
      } else if (header === "5m+") {
        return this.puzzleTime >= 300;
      }

      return (
        this.puzzleTime >= header[0] * 60 && this.puzzleTime < header[2] * 60
      );
    },
    pageChange(newPageIndex, oldPageIndex, behavior, newPageName) {
      this.title =
        newPageName === "Overall"
          ? `Puzzle No. ${this.puzzleNumber} Benchmarks`
          : `Level ${this.status.level} ${this.status.mode} Mode Benchmarks`;
      if (behavior === "smooth") {
        this.titleFade =
          oldPageIndex < newPageIndex ? "move-right-fade" : "move-left-fade";
      } else {
        this.titleFade = null;
      }
    },
    getBarWidth(value, metric) {
      try {
        let subtractWidth = metric === "play_time" ? "8vh" : "6vh";
        const decimal =
          parseFloat(value) /
          Math.max(
            Object.values(this.benchmarkData[metric]?.benchmarks).reduce(
              (maxValue, value) => {
                if (maxValue < value) {
                  maxValue = value;
                }
                return maxValue;
              },
              0
            )
          );
        return `calc((100% - ${subtractWidth} - 0.5vh) * ${decimal} )`;
      } catch {
        return "auto";
      }
    },
    getBenchmarkPossbilities(metric) {
      if (metric == null) {
        return [];
      }
      if (metric === "hints_used") {
        return Array.from({ length: 4 }, (_, i) => i);
      }
      if (metric === "found_sequences") {
        const length = this.status.puzzleInfo.possible_sequences.length + 1;
        return Array.from({ length }, (_, i) => i);
      }
      if (metric === "play_time") {
        const keys = Object.keys(
          this.benchmarkData?.matching?.[metric]?.benchmarks || {}
        );
        keys.sort((a, b) => {
          if (a === "<1m") return -1; // '<1m' should come first
          if (b === "<1m") return 1; // '<1m' should come first
          if (a.endsWith("m+")) return 1; // '5m+' should come after ranges
          if (b.endsWith("m+")) return -1; // '5m+' should come after ranges

          // Extract numbers for other cases
          const [aStart] = a.split("-").map((s) => parseFloat(s));
          const [bStart] = b.split("-").map((s) => parseFloat(s));
          return aStart - bStart;
        });
        return keys;
      }
      if (metric === "error") {
        const keys = Object.keys(
          this.benchmarkData?.matching?.[metric]?.benchmarks || {}
        );
        keys.sort();
        return keys;
      }

      return Object.keys(this.benchmarkData[metric]?.benchmarks);
    },
    getOverallBenchmarkClass(level, mode) {
      return {
        "overall-highlight":
          level === this.status.level && mode === this.status.mode,
      };
    },
    getOverallBenchmark(level, mode, units = "%") {
      if (!this.benchmarkDataAvailable("overall")) {
        return null;
      }
      if (!(level in this.benchmarkData.overall?.benchmarks)) {
        return 0 + units;
      }
      if (!(mode in this.benchmarkData.overall?.benchmarks[level])) {
        return 0 + units;
      }
      return this.benchmarkData.overall?.benchmarks[level][mode] + units;
    },
    getMatchingBenchmark(metric, index) {
      try {
        return this.benchmarkData[metric]?.benchmarks[index]
          ? this.benchmarkData[metric]?.benchmarks[index] + "%"
          : "0%";
      } catch {
        return "0%";
      }
    },
    speedStatement() {
      if (!this.benchmarkDataAvailable("speed")) {
        return;
      }
      let description = `Your completion time of
                <strong>${this.elapsedTimeFormater(
                  this.puzzleTime
                )}</strong> ranks in
                the <strong>top ${this.speedBenchmark}%</strong> of players, `;
      if (
        this.benchmarkData?.matching?.speed?.sample_size ===
          this.benchmarkData?.matching?.speed?.max_sample_size &&
        this.benchmarkData?.matching?.speed?.sample_size !== undefined
      ) {
        description += `based on a sample of ${numberToString(
          this.benchmarkData?.matching?.speed?.sample_size
        )} completions of Puzzle <strong>No. ${this.puzzleNumber}</strong>.`;
      } else {
        description += `based on the ${numberToString(
          this.benchmarkData?.matching?.speed?.sample_size
        )} completions of Puzzle <strong>No. ${
          this.puzzleNumber
        }</strong> so far.`;
      }
      return description;
    },
    overallBenchmarksStatement() {
      if (!this.benchmarkDataAvailable("overall")) {
        return;
      }
      let description = `Breakdown of level and mode selections for Puzzle <strong> No. ${this.puzzleNumber}</strong>,`;
      if (
        this.benchmarkData.overall?.sample_size ===
        this.benchmarkData.overall?.max_sample_size
      ) {
        description += ` based on a sample of ${numberToString(
          this.benchmarkData.overall?.sample_size
        )} completions.`;
      } else {
        description += ` based on the ${numberToString(
          this.benchmarkData.overall?.sample_size
        )} completions so far.`;
      }
      return description;
    },
    matchingBenchmarkStatement(metric) {
      if (!this.benchmarkDataAvailable(metric)) {
        return;
      }
      if (metric === "speed") {
        return this.speedStatement();
      }
      const metricStr =
        metric === "found_sequences"
          ? "number of sequences found"
          : metric === "hints_used"
          ? "number of hints used"
          : metric === "error"
          ? "number of errors made"
          : "time to complete the puzzle among those who didn't use any hints";

      let description = `Percentage of players grouped by the ${metricStr}.\n`;
      if (
        this.benchmarkData?.matching?.[metric]?.sample_size ===
        this.benchmarkData?.matching?.[metric]?.max_sample_size
      ) {
        description += `Based on a sample of ${numberToString(
          this.benchmarkData?.matching?.[metric]?.sample_size
        )} completions of Puzzle <strong>No. ${this.puzzleNumber}</strong>.`;
      } else {
        description += ` Based on the ${numberToString(
          this.benchmarkData?.matching?.[metric]?.sample_size
        )} completions of Puzzle <strong>No. ${
          this.puzzleNumber
        }</strong> so far.`;
      }
      return description;
    },
    benchmarkDataLoading(metric) {
      return (
        this.benchmarkData !== null &&
        metric in this.benchmarkData &&
        this.benchmarkData[metric] === null
      );
    },
    benchmarkDataAvailable(metric) {
      if (metric === "overall") {
        return this.benchmarkData?.overall?.sample_size > 1;
      }
      return this.benchmarkData?.matching?.[metric]?.sample_size > 1;
    },
  },
  computed: {
    maxBenchmarkLabels() {
      return Object.values(this.benchmarkData?.matching || {}).reduce(
        (acc, label) => {
          acc = Math.max(Object.keys(label.benchmarks).length, acc);
          return acc;
        },
        0
      );
    },
    selectedMetricDescription() {
      const key = this.selectedMetric ?? "init_key";
      const desc = this.matchingBenchmarkStatement(key);
      return { key, desc };
    },
    playerMetricResult() {
      if (this.selectedMetric === "play_time") {
        return this.playerTimeBin();
      } else if (this.selectedMetric === "found_sequences") {
        return this.foundSequencesCount;
      } else if (this.selectedMetric === "hints_used") {
        return this.revealedHints;
      } else if (this.selectedMetric === "error") {
        return this.totalErrors;
      }
      return "";
    },
    metricTitles() {
      return Object.values(this.metricTitlesLookup);
    },
    speedBenchmark() {
      if (!this.benchmarkDataAvailable("speed") || this.revealedHints > 0) {
        return null;
      }
      let result = null;
      const benchmarks = this.benchmarkData?.matching?.speed?.benchmarks || {};
      if (this.benchmarkData?.matching?.speed?.sample_size <= 20) {
        return null;
      }
      const speedIndex = Object.entries(benchmarks).reduce(
        (targetKey, [key, value]) => {
          if (
            this.puzzleTime <= value &&
            (targetKey === null || value < benchmarks[targetKey])
          ) {
            return key;
          }
          return targetKey;
        },
        null
      );
      if (speedIndex) {
        result = parseFloat(speedIndex.substring(1));
      }

      return result;
    },
  },
};
</script>

<style scoped>
.centered-text {
  white-space: pre-wrap;
}
.speed-benchmark {
  display: flex;
  align-items: center;
  height: 11.4vh;
  overflow: hidden;
}
.speed-benchmark-container {
  position: relative;
}
.speed-benchmark .label {
  border-right: 5px solid var(--border-color);
  text-align: center;
}
.single-axis-benchmark {
  display: flex;
  gap: 0.5vh;
  padding: 0.5vh;
  border-radius: 0.5vh;
}
.single-axis-benchmark.selected {
  border: 1px solid var(--border-color);
}

.single-axis-benchmark.selected .bar {
  background: var(--border-color);
}
.single-axis-benchmark .header {
  flex: 0 0 auto;
  text-align: right;
}
.single-axis-benchmark .bar {
  flex: 0 0 auto;
  border-radius: 5px;
  background-color: var(--disabled);
}
.single-axis-benchmark .label {
  flex: 1 0 auto;
  text-align: left;
}
#overall-benchmarks {
  text-align: center;
  vertical-align: middle;
  padding: 0;
  margin: 0;
  width: 100%;
  border-collapse: collapse;
}
.overall-level-label {
  text-align: right;
  width: fit-content;
}
.overall-highlight {
  background: var(--border-color);
  color: var(--modal-background-color) !important;
}
#overall-benchmarks td,
#overall-benchmarks th {
  padding: 0;
  margin: 0;
}

#overall-benchmarks td {
  width: calc((100% - 1%) / 2);
}
#overall-benchmarks tr:not(:last-child) td,
#overall-benchmarks tr:not(:last-child) th {
  border-bottom: 1px solid var(--border-color);
}

#overall-benchmarks td:not(:last-child),
#overall-benchmarks th:not(:last-child):not(:first-child) {
  border-right: 1px solid var(--border-color);
}
#overall-benchmarks tbody th:first-child {
  width: 1%; /* Ensures the first header column is as small as possible */
  white-space: nowrap; /* Prevents wrapping in the first column */
}

#overall-benchmarks td div,
.speed-benchmark .label {
  font-size: 3.5vh;
  font-weight: bold;
  color: var(--border-color);
  font-family: "Fira Sans", system-ui;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5vh 1vh;
  padding: 1vh;
}
#overall-benchmarks td div {
  border-radius: 10px;
}
#overall-benchmarks th {
  font-size: 1.75vh;
  white-space: pre-line;
  font-weight: normal;
}
.stats-flex {
  gap: 0.875vh;
  display: flex;
  flex-direction: column;
}
#container {
  justify-content: center;
}
.stats-box {
  display: flex;
  gap: 25px;
  justify-content: center;
  align-items: center;
  margin: 0 20px;
  height: 10vh;
}
.stats-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 2px;
  overflow: visible;
}

.stats-chart {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.stats-chart-row {
  display: flex;
  gap: 5px;
}

.stats-chart-row-label {
  text-align: right;
  font-weight: bold;
  width: 7vh;
}

.bar-highlight {
  background: var(--border-color);
  padding: 0 1vh;
  position: relative;
}

.stats-img {
  height: 8vh;
}

.share {
  background: var(--share-color);
  color: var(--modal-background-color);
}
.archive {
  background: var(--border-color);
  color: var(--modal-background-color);
}
button img {
  height: 2.5vh;
}
.privacy-note {
  font-size: 1.25em;
  margin: 0 8.5vmin;
}
.knotilus-logo img {
  height: 1em;
}

.lvl-stats-container {
  position: relative;
}

.puzzle-time {
  font-weight: bold;
  font-size: 3vh;
  font-family: "Fira Sans", system-ui;
}
#leaderboard {
  border-collapse: collapse;
}
#leaderboard th,
#leaderboard td {
  text-align: center;
  border-bottom: 1px solid var(--primary-color);
  padding: 1vh 0;
}
.benchmark-description {
  display: flex;
  align-items: center;
  justify-content: center;
  inset: 0;
  text-align: center;
  /* margin: 0 3vh; */
  position: absolute;
}

.benchmark-description-container {
  position: relative;
  height: 4.5em;
}


.flatten-enter-from,
.flatten-leave-to {
  opacity: 0;
  height: 0vh;
}

.flatten-enter-active,
.flatten-leave-active {
  transition: all 0.5s ease;
}
</style>
